import { type FC, createElement, useRef, useEffect, useState } from 'react';
import BrightcoveReactPlayer from '@brightcove/react-player-loader';
import { ConfigService } from '../core';
import Tag, { TagStyle } from '../Tag/Tag';
import { Theme } from '../types/enums';
import styles from './BrightcovePlayer.module.scss';
import {
  type BrightcovePlayerAttributes,
  type BrightcovePlayerProps,
  type BrightcovePlayerRef,
  buttonSizeMapping,
} from './types/propTypes';
import { PlayButtonSize } from './types/enums';
import { changeButtonStyle, handleOnSuccess } from './BrightcovePlayerHelper';

const BrightcovePlayer: FC<BrightcovePlayerProps> = ({
  poster = '',
  loop = false,
  muted = false,
  autoplay = false,
  showDuration = false,
  videoId,
  playerId,
  accountId,
  playerWidth = '',
  playerHeight = '',
  isPlayerResponsive = true,
  containerClassName = '',
  playerRef,
  playerOnReadyHandler,
  playOnButtonClick = true,
  playButtonSize = PlayButtonSize.LARGE,
}) => {
  const config = ConfigService.getConfigs();
  const brighcoveButtonRef = useRef<HTMLButtonElement>();
  const [duration, setDuration] = useState<string>('');
  function processStyle(ref?: HTMLButtonElement) {
    brighcoveButtonRef.current = ref;
    if (ref) {
      changeButtonStyle({
        element: ref,
        ...{ ...buttonSizeMapping[playButtonSize] },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } as any);
    }
  }

  useEffect(() => {
    if (brighcoveButtonRef.current) {
      processStyle(brighcoveButtonRef.current);
    }
  }, [playButtonSize]);

  const reactPlayerLoader = createElement(BrightcoveReactPlayer, {
    options: {
      autoplay,
      muted,
      loop,
      fluid: true,
      height: playerWidth,
      width: playerHeight,
      poster,
      responsive: isPlayerResponsive,
    },
    embedOptions: {
      responsive: isPlayerResponsive,
    },
    accountId: accountId ?? config.NEXT_PUBLIC_BRIGHTCOVE_ACCOUNT_ID,
    playerId: playerId ?? config.NEXT_PUBLIC_BRIGHTCOVE_PLAYER_ID,
    videoId,
    onSuccess: function (success) {
      handleOnSuccess(success, showDuration, setDuration, processStyle, playerRef, playerOnReadyHandler);
    },
  } as BrightcovePlayerAttributes);

  const containerStyles = {
    height: playerHeight && playerHeight !== '' ? playerHeight : 'auto',
    width: playerWidth && playerWidth !== '' ? playerWidth : 'auto',
  };

  return (
    <div
      className={`${styles['brightcove-player-container']} ${
        playOnButtonClick ? styles['brightcove-wrapper-play-button'] : ''
      } ${containerClassName}`}
      id={'brightcove-container'}
      data-testid="brightcove-container"
      style={containerStyles}
    >
      {reactPlayerLoader}
      {duration && (
        <Tag
          text={duration}
          style={TagStyle.OUTLINE}
          theme={Theme.WHITE}
          className={styles['brightcove-player-duration-tag']}
        />
      )}
    </div>
  );
};

export default BrightcovePlayer;
export type { BrightcovePlayerProps, BrightcovePlayerRef };
export { PlayButtonSize };
